import { useParams, useSearchParams } from 'react-router-dom'

import { useGetCustomer } from '@jotta/grpc-connect-client/customer'
import { SubscriptionType } from '@jotta/grpc-connect-openapi/customer'
import type { GetPaymentInfoResponse } from '@jotta/grpc-connect-openapi/payment'
import {
  RecurringSubscriptionInterval,
  SubscriptionInterval,
} from '@jotta/grpc-connect-openapi/payment'
import { AppLayout } from '@jotta/ui/AppLayout'
import { LoadingOverlay } from '@jotta/ui/LoadingOverlay'
import { Redirect } from '@jotta/ui/Redirect'
import { useBrandCode } from '@jotta/ui/useBrandTheme'
import { exhaustiveGuard } from '@jotta/utils/exhaustive'
import { isIapRequested } from '@jotta/utils/iapSession'
import { isSubscriptionHidden } from '@jotta/utils/isSubscriptionHidden'

import { PrivateLayout } from '../components/Layout/PrivateLayout'
import { usePaymentInfo } from '../payment/usePaymentInformation'

import { androidProductsDeeplink } from './androidProductsDeeplink'
import { AndroidProductsRedirect } from './AndroidProductsRedirect'
import { ConfirmUpgrade } from './ConfirmUpgrade'
import { Convert } from './Convert'
import { Upgrade } from './Upgrade'

function resolveInterval(
  queryParam: string | null,
  getPaymentInfoResponse: GetPaymentInfoResponse | undefined,
): RecurringSubscriptionInterval | undefined {
  switch (queryParam) {
    case 'monthly':
      return RecurringSubscriptionInterval.MONTHLY_RECURRING
    case 'yearly':
      return RecurringSubscriptionInterval.YEARLY_RECURRING
  }

  switch (getPaymentInfoResponse?.subscriptionInterval) {
    case undefined:
      return undefined
    case SubscriptionInterval.MONTHLY:
      return RecurringSubscriptionInterval.MONTHLY_RECURRING
    default:
      return RecurringSubscriptionInterval.YEARLY_RECURRING
  }
}

export const UpgradeRoute = ({ convertIntent }: { convertIntent: boolean }) => {
  const brandCode = useBrandCode()
  const { data: getPaymentInfoResponse } = usePaymentInfo()
  const { data: customer } = useGetCustomer()
  const { productCode } = useParams()
  const [search] = useSearchParams()
  const recurringInterval = resolveInterval(
    search.get('interval'),
    getPaymentInfoResponse,
  )
  const changeSubscriptionOption =
    getPaymentInfoResponse?.changeSubscriptionOption
  // Convert, expire, and renew reuse this route and stay on the web.
  // An Android app visit without ?iap=true has no web upgrade page.
  if (!convertIntent && isSubscriptionHidden()) {
    return <Redirect to="/web/account" replace />
  }
  if (!convertIntent && customer && getPaymentInfoResponse) {
    const productsUrl = androidProductsDeeplink({
      brandCode,
      userAgent: navigator.userAgent,
      paymentMethodCase:
        getPaymentInfoResponse.paymentInfo?.paymentMethod?.case,
      isFreeAccount: customer.subscriptionType === SubscriptionType.FREE,
      iapRequested: isIapRequested(),
    })
    if (productsUrl) {
      return <AndroidProductsRedirect url={productsUrl} />
    }
  }

  if (
    !customer ||
    !recurringInterval ||
    !changeSubscriptionOption ||
    !changeSubscriptionOption.case
  ) {
    return <LoadingOverlay id="UpgradeRoute" open />
  }

  switch (changeSubscriptionOption.case) {
    case 'contactBusinessAdmin':
    case 'manageInAppStore':
    case 'manageInPlayStore':
    case 'contactReseller':
    case 'contactSupport':
    case 'manageSubscriptionExternally':
      return <Redirect to="/web/billing" replace />
    case 'convertFromReseller':
      return <Convert />
    case 'saSubscription':
    case 'normalUpgrade':
    case 'manageInBusinessPages':
    case 'contactFamilyOwner':
      if (convertIntent && customer.subscriptionType != SubscriptionType.FREE) {
        return <Convert />
      }
      return productCode ? (
        <ConfirmUpgrade
          productCode={productCode}
          subscriptionInterval={recurringInterval}
        />
      ) : (
        <Upgrade />
      )
    default:
      return exhaustiveGuard(changeSubscriptionOption)
  }
}
